<template>
  <v-dialog
    :value="value"
    :max-width="isDigitalMode ? 1280 : 1180"
    scrollable
    persistent
    content-class="dialog-border-radius kkm-addressing-dialog"
    :class="{ 'kkm-addressing-dialog--digital': isDigitalMode }"
    @input="$emit('input', $event)"
  >
    <v-card>
      <v-card-title class="kkm-addressing-dialog__title">
        Коммутатор координатно-матричный (тайминги)
        <v-spacer />
        <v-btn icon @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <template v-if="isDigitalMode">
          <v-row align="end" class="mb-4">
            <v-col cols="12" sm="4">
              <v-text-field
                v-model="localFirstLocationIdStr"
                label="Квартиры в подъезде начинаются с номера"
                type="text"
                inputmode="numeric"
                pattern="[0-9]*"
                outlined
                dense
                hide-details
                :counter="`${localFirstLocationId || 0}/50000`"
                @input="onFirstLocationInput"
              />
            </v-col>
            <v-col cols="12" sm="4">
              <v-btn
                color="primary"
                outlined
                class="text-none"
                @click="fillDigitTable"
              >
                <v-icon left small>mdi-table-plus</v-icon>
                Заполнить
              </v-btn>
            </v-col>
          </v-row>

          <h6 class="kkm-addressing-dialog__table-title mb-1">
            Таблица адресации цифровой линии (1–255)
          </h6>
          <div class="kkm-matrix-wrap kkm-matrix-wrap--digital">
            <table class="kkm-matrix kkm-matrix--digital">
              <thead>
                <tr>
                  <th class="kkm-matrix__corner" />
                  <th
                    v-for="col in digitCols"
                    :key="`dh-h-${col}`"
                    class="kkm-matrix__head"
                  >
                    {{ col + 1 }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in digitRows" :key="`dh-r-${row}`">
                  <th class="kkm-matrix__head">{{ row + 1 }}</th>
                  <td
                    v-for="col in digitCols"
                    :key="`dh-${col}-${row}`"
                    class="kkm-matrix__cell"
                  >
                    <input
                      type="text"
                      inputmode="numeric"
                      pattern="[0-9]*"
                      class="kkm-matrix__input-native"
                      :value="digitCellDisplayValue(col, row)"
                      @input="setDigitCell(col, row, $event.target.value)"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>

        <template v-else>
          <v-row align="end" class="mb-4">
            <v-col cols="12" sm="4">
              <v-autocomplete
                v-model="localKkmtype"
                :items="kkmTypes"
                item-text="text"
                item-value="value"
                label="Тип ККМ"
                outlined
                dense
                hide-details
              />
            </v-col>
            <v-col cols="12" sm="4">
              <v-text-field
                v-model="localFirstLocationIdStr"
                label="Квартиры в подъезде начинаются с номера"
                type="text"
                inputmode="numeric"
                pattern="[0-9]*"
                outlined
                dense
                hide-details
                :counter="`${localFirstLocationId || 0}/50000`"
                @input="onFirstLocationInput"
              />
            </v-col>
            <v-col cols="12" sm="4">
              <v-btn
                color="primary"
                outlined
                class="text-none"
                :disabled="!visibleHundredKeys.length"
                @click="fillAllTables"
              >
                <v-icon left small>mdi-table-plus</v-icon>
                Заполнить
              </v-btn>
            </v-col>
          </v-row>

          <v-row
            v-if="isManualKkmtype"
            class="kkm-addressing-dialog__timings mb-2"
          >
            <v-col cols="6" sm="3">
              <v-text-field
                v-model.number="localTimings.kkmTimingsStartMs"
                label="Старт, мс"
                type="number"
                outlined
                dense
                hide-details
              />
            </v-col>
            <v-col cols="6" sm="3">
              <v-text-field
                v-model.number="localTimings.kkmTimingsStopMs"
                label="Стоп, мс"
                type="number"
                outlined
                dense
                hide-details
              />
            </v-col>
            <v-col cols="6" sm="3">
              <v-text-field
                v-model.number="localTimings.kkmTimingsCountPulse0Us"
                label="Импульс 0, мкс"
                type="number"
                outlined
                dense
                hide-details
              />
            </v-col>
            <v-col cols="6" sm="3">
              <v-text-field
                v-model.number="localTimings.kkmTimingsCountPulse1Us"
                label="Импульс 1, мкс"
                type="number"
                outlined
                dense
                hide-details
              />
            </v-col>
          </v-row>

          <v-row
            v-if="visibleHundredKeys.length"
            class="kkm-addressing-dialog__tables-row"
          >
            <v-col
              v-for="hundredKey in visibleHundredKeys"
              :key="hundredKey"
              cols="12"
              :sm="visibleHundredKeys.length === 1 ? 12 : 6"
              class="kkm-addressing-dialog__table-block"
            >
              <h6 class="kkm-addressing-dialog__table-title mb-1">
                Таблица адресации ККМ для {{ hundredOrdinal(hundredKey) }} сотни
              </h6>
              <div class="kkm-matrix-wrap">
                <table class="kkm-matrix">
                  <thead>
                    <tr>
                      <th class="kkm-matrix__corner" />
                      <th
                        v-for="col in matrixCols"
                        :key="`${hundredKey}-${col}`"
                        class="kkm-matrix__head"
                      >
                        {{ col }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(rowLabel, row) in matrixRows" :key="`${hundredKey}-${rowLabel}`">
                      <th class="kkm-matrix__head">{{ rowLabel }}</th>
                      <td
                        v-for="(colLabel, col) in matrixCols"
                        :key="`${hundredKey}-${rowLabel}-${colLabel}`"
                        class="kkm-matrix__cell"
                      >
                        <input
                          type="text"
                          inputmode="numeric"
                          pattern="[0-9]*"
                          class="kkm-matrix__input-native"
                          :value="cellDisplayValue(hundredKey, col, row)"
                          @input="setCell(hundredKey, col, row, $event.target.value)"
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </v-col>
          </v-row>

          <p v-else class="text--secondary">
            Для выбранного типа ККМ таблицы адресации не используются.
          </p>
        </template>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn text class="text-none" @click="close">Отмена</v-btn>
        <v-btn color="primary" class="text-none" @click="apply">Применить</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  KKM_TYPE_MANUAL,
  KKM_TYPES,
  MATRIX_COLS,
  MATRIX_ROWS,
  HUNDRED_KEYS,
  DIGIT_HANDSET_COLS,
  DIGIT_HANDSET_ROWS,
  createEmptyHundred,
  createEmptyHundreds,
  createEmptyDigitHandset,
  getCellIndex,
  getDigitHandsetIndex,
  fillHundredTable,
  fillDigitHandset,
  getVisibleHundredKeys,
  hundredsForKkmtype,
  normalizeDigitHandset,
  isDigitHandsetEmpty,
} from './kkmConsts';

export default {
  name: 'KkmAddressingModal',
  props: {
    value: { type: Boolean, default: false },
    workingMode: { type: Number, default: 1 },
    kkmtype: { type: Number, default: 0 },
    firstLocationId: { type: Number, default: 1 },
    hundreds: { type: Object, required: true },
    digitHandset: { type: Array, default: () => createEmptyDigitHandset() },
    timings: {
      type: Object,
      default: () => ({
        kkmTimingsStartMs: 0,
        kkmTimingsStopMs: 0,
        kkmTimingsCountPulse0Us: 0,
        kkmTimingsCountPulse1Us: 0,
      }),
    },
  },
  data () {
    return {
      kkmTypes: KKM_TYPES,
      matrixCols: MATRIX_COLS,
      matrixRows: MATRIX_ROWS,
      digitCols: Array.from({ length: DIGIT_HANDSET_COLS }, (_, i) => i),
      digitRows: Array.from({ length: DIGIT_HANDSET_ROWS }, (_, i) => i),
      localKkmtype: 0,
      localFirstLocationId: 1,
      localFirstLocationIdStr: '1',
      localHundreds: createEmptyHundreds(),
      localDigitHandset: createEmptyDigitHandset(),
      localTimings: {
        kkmTimingsStartMs: 0,
        kkmTimingsStopMs: 0,
        kkmTimingsCountPulse0Us: 0,
        kkmTimingsCountPulse1Us: 0,
      },
    };
  },
  computed: {
    isDigitalMode () {
      return this.workingMode === 2;
    },
    visibleHundredKeys () {
      return getVisibleHundredKeys(this.localKkmtype);
    },
    isManualKkmtype () {
      return this.localKkmtype === KKM_TYPE_MANUAL;
    },
  },
  watch: {
    value: {
      handler (open) {
        if (open) {
          this.syncFromProps();
        }
      },
      immediate: true,
    },
    localKkmtype (newType, oldType) {
      if (oldType === undefined || newType === oldType || this.isDigitalMode) {
        return;
      }
      this.fillNewlyVisibleHundreds(oldType, newType);
    },
  },
  methods: {
    getCellIndex,
    ensureAllHundredKeys () {
      HUNDRED_KEYS.forEach((key) => {
        if (!Array.isArray(this.localHundreds[key])) {
          this.$set(this.localHundreds, key, createEmptyHundred());
        }
      });
    },
    syncFromProps () {
      this.localKkmtype = this.kkmtype;
      this.localFirstLocationId = this.firstLocationId ?? 1;
      this.localFirstLocationIdStr = String(this.localFirstLocationId);
      this.localTimings = { ...this.timings };

      if (this.isDigitalMode) {
        this.localDigitHandset = normalizeDigitHandset(this.digitHandset);
        if (isDigitHandsetEmpty(this.localDigitHandset)) {
          this.fillDigitTable();
        }
        return;
      }

      this.localHundreds = hundredsForKkmtype(this.hundreds, this.kkmtype);
      this.ensureAllHundredKeys();
      this.fillEmptyVisibleHundreds();
    },
    isHundredEmpty (table) {
      return !Array.isArray(table) || table.every((v) => Number(v) === 0);
    },
    fillHundredByKey (key) {
      const hundredIndex = this.hundredOrdinal(key);
      this.$set(
        this.localHundreds,
        key,
        fillHundredTable(hundredIndex, this.localFirstLocationId),
      );
    },
    fillEmptyVisibleHundreds () {
      this.visibleHundredKeys.forEach((key) => {
        if (this.isHundredEmpty(this.localHundreds[key])) {
          this.fillHundredByKey(key);
        }
      });
    },
    fillNewlyVisibleHundreds (oldType, newType) {
      this.ensureAllHundredKeys();
      const oldKeys = getVisibleHundredKeys(oldType);
      const newKeys = getVisibleHundredKeys(newType);

      newKeys.forEach((key) => {
        if (!oldKeys.includes(key)) {
          this.fillHundredByKey(key);
        }
      });
    },
    buildHundredsPayload () {
      return hundredsForKkmtype(this.localHundreds, this.localKkmtype);
    },
    onFirstLocationInput (value) {
      const digits = String(value).replace(/\D/g, '');
      this.localFirstLocationIdStr = digits;
      this.localFirstLocationId = digits === '' ? 1 : Number(digits);
    },
    cellDisplayValue (hundredKey, col, row) {
      const table = this.localHundreds[hundredKey];
      if (!Array.isArray(table)) {
        return '';
      }
      const v = table[getCellIndex(col, row)];
      return v === 0 ? '0' : String(v ?? '');
    },
    hundredOrdinal (hundredKey) {
      return Number(hundredKey.replace('hundred', ''));
    },
    setCell (hundredKey, col, row, value) {
      if (!Array.isArray(this.localHundreds[hundredKey])) {
        this.$set(this.localHundreds, hundredKey, createEmptyHundred());
      }
      const idx = getCellIndex(col, row);
      const digits = String(value).replace(/\D/g, '');
      const num = digits === '' ? 0 : Number(digits);
      this.$set(this.localHundreds[hundredKey], idx, Number.isNaN(num) ? 0 : num);
    },
    fillAllTables () {
      this.visibleHundredKeys.forEach((key) => {
        const hundredIndex = this.hundredOrdinal(key);
        this.$set(
          this.localHundreds,
          key,
          fillHundredTable(hundredIndex, this.localFirstLocationId),
        );
      });
    },
    digitCellDisplayValue (col, row) {
      const v = this.localDigitHandset[getDigitHandsetIndex(col, row)];
      return v === 0 ? '0' : String(v ?? '');
    },
    setDigitCell (col, row, value) {
      const idx = getDigitHandsetIndex(col, row);
      const digits = String(value).replace(/\D/g, '');
      const num = digits === '' ? 0 : Number(digits);
      this.$set(this.localDigitHandset, idx, Number.isNaN(num) ? 0 : num);
    },
    fillDigitTable () {
      this.localDigitHandset = fillDigitHandset(this.localFirstLocationId);
    },
    close () {
      this.$emit('input', false);
    },
    apply () {
      if (this.isDigitalMode) {
        this.$emit('apply', {
          digitHandset: [...this.localDigitHandset],
          firstLocationId: this.localFirstLocationId,
        });
        this.$emit('input', false);
        return;
      }

      const payload = {
        kkmtype: this.localKkmtype,
        firstLocationId: this.localFirstLocationId,
        hundreds: this.buildHundredsPayload(),
      };
      if (this.isManualKkmtype) {
        payload.timings = { ...this.localTimings };
      }
      this.$emit('apply', payload);
      this.$emit('input', false);
    },
  },
};
</script>

<style lang="scss">
.kkm-addressing-dialog {
  width: min(950px, 92vw) !important;
  max-width: min(950px, 92vw) !important;
}

.kkm-addressing-dialog--digital {
  width: min(1180px, 96vw) !important;
  max-width: min(1180px, 96vw) !important;
}
</style>

<style lang="scss" scoped>
.kkm-addressing-dialog__title {
  font-size: 1.1rem;
  font-weight: 500;
}

.kkm-addressing-dialog__tables-row {
  margin-top: 0;
}

.kkm-addressing-dialog__table-block {
  margin-bottom: 8px;
}

.kkm-addressing-dialog__table-title {
  font-size: 0.8rem;
  font-weight: 500;
  color: #546e7a;
}

.kkm-matrix-wrap {
  overflow-x: auto;
  max-width: 100%;
}

.kkm-matrix-wrap--digital {
  max-width: 100%;
}

.kkm-matrix {
  border-collapse: collapse;
  font-size: 10px;
  table-layout: fixed;
  width: 100%;
  max-width: 400px;

  &--digital {
    max-width: none;
    width: max-content;
  }

  &__corner {
    width: 22px;
  }

  &__head {
    padding: 1px 2px;
    text-align: center;
    font-weight: 500;
    color: #546e7a;
    font-size: 9px;
  }

  &__cell {
    padding: 1px;
    vertical-align: middle;
  }
}

.kkm-matrix__input-native {
  box-sizing: border-box;
  width: 100%;
  max-width: 36px;
  height: 24px;
  margin: 0 auto;
  display: block;
  padding: 0 2px;
  border: 1px solid rgba(0, 0, 0, 0.24);
  border-radius: 4px;
  text-align: center;
  font-size: 11px;
  line-height: 24px;
  font-family: inherit;
  color: rgba(0, 0, 0, 0.87);
  background: #fff;
  -moz-appearance: textfield;
  appearance: textfield;

  &:focus {
    outline: none;
    border-color: var(--v-primary-base, #1976d2);
    border-width: 2px;
    padding: 0 1px;
  }

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
}
</style>
